import {
  BuildCartDTO,
  BuildCartPhase,
  BuildCartProgress,
} from './build-cart.types';

/**
 * Build Cart Entity
 *
 * Represents the business logic and state for building a cart.
 * This entity manages phase progression, progress tracking, and validation.
 */
export class BuildCartEntity {
  private _caseId: string;
  private _currentPhase: BuildCartPhase;
  private _selectedCartId?: string;
  private _selectedCartLocation?: string;
  private _selectedCartReferenceNumber?: string;
  private _traysLoaded: number;
  private _traysRequired: number;
  private _instrumentsSuppliesLoaded: number;
  private _instrumentsSuppliesRequired: number;

  constructor(dto: BuildCartDTO) {
    this._caseId = dto.caseId;
    this._currentPhase = dto.currentPhase;
    this._selectedCartId = dto.selectedCartId;
    this._selectedCartLocation = dto.selectedCartLocation;
    this._selectedCartReferenceNumber = dto.selectedCartReferenceNumber;
    this._traysLoaded = dto.traysLoaded;
    this._traysRequired = dto.traysRequired;
    this._instrumentsSuppliesLoaded = dto.instrumentsSuppliesLoaded;
    this._instrumentsSuppliesRequired = dto.instrumentsSuppliesRequired;
  }

  /**
   * Get the case ID
   */
  get caseId(): string {
    return this._caseId;
  }

  /**
   * Get the current phase
   */
  get currentPhase(): BuildCartPhase {
    return this._currentPhase;
  }

  /**
   * Get the current phase (alias for consistency)
   */
  public getCurrentPhase(): BuildCartPhase {
    return this._currentPhase;
  }

  /**
   * Get the selected cart ID
   */
  get selectedCartId(): string | undefined {
    return this._selectedCartId;
  }

  /**
   * Get the selected cart location
   */
  get selectedCartLocation(): string | undefined {
    return this._selectedCartLocation;
  }

  /**
   * Get the selected cart reference number
   */
  get selectedCartReferenceNumber(): string | undefined {
    return this._selectedCartReferenceNumber;
  }

  /**
   * Get trays loaded count
   */
  get traysLoaded(): number {
    return this._traysLoaded;
  }

  /**
   * Get trays required count
   */
  get traysRequired(): number {
    return this._traysRequired;
  }

  /**
   * Get instruments/supplies loaded count
   */
  get instrumentsSuppliesLoaded(): number {
    return this._instrumentsSuppliesLoaded;
  }

  /**
   * Get instruments/supplies required count
   */
  get instrumentsSuppliesRequired(): number {
    return this._instrumentsSuppliesRequired;
  }

  /**
   * Check if a cart is selected
   */
  public hasCartSelected(): boolean {
    return !!this._selectedCartId && !!this._selectedCartLocation;
  }

  /**
   * Select a cart
   */
  public selectCart(cartId: string, location: string): void {
    this._selectedCartId = cartId;
    this._selectedCartLocation = location;
  }

  /**
   * Update tray progress
   */
  public updateTrayProgress(loaded: number, required: number): void {
    if (loaded < 0) throw new Error('Trays loaded cannot be negative');
    if (required < 0) throw new Error('Trays required cannot be negative');
    if (loaded > required)
      throw new Error('Trays loaded cannot exceed required');

    this._traysLoaded = loaded;
    this._traysRequired = required;
  }

  /**
   * Update instruments/supplies progress
   */
  public updateInstrumentsProgress(loaded: number, required: number): void {
    if (loaded < 0)
      throw new Error('Instruments/supplies loaded cannot be negative');
    if (required < 0)
      throw new Error('Instruments/supplies required cannot be negative');
    if (loaded > required)
      throw new Error('Instruments/supplies loaded cannot exceed required');

    this._instrumentsSuppliesLoaded = loaded;
    this._instrumentsSuppliesRequired = required;
  }

  /**
   * Set the current phase
   */
  public setPhase(phase: BuildCartPhase): void {
    if (phase !== BuildCartPhase.CART && phase !== BuildCartPhase.TRAY) {
      throw new Error('Phase must be either "cart" or "tray"');
    }
    this._currentPhase = phase;
  }

  /**
   * Get progress information
   */
  public getProgress(): BuildCartProgress {
    return {
      traysLoaded: this._traysLoaded,
      traysRequired: this._traysRequired,
      instrumentsSuppliesLoaded: this._instrumentsSuppliesLoaded,
      instrumentsSuppliesRequired: this._instrumentsSuppliesRequired,
    };
  }

  /**
   * Convert to DTO for serialization
   */
  public toDTO(): BuildCartDTO {
    return {
      caseId: this._caseId,
      currentPhase: this._currentPhase,
      selectedCartId: this._selectedCartId,
      selectedCartLocation: this._selectedCartLocation,
      traysLoaded: this._traysLoaded,
      traysRequired: this._traysRequired,
      instrumentsSuppliesLoaded: this._instrumentsSuppliesLoaded,
      instrumentsSuppliesRequired: this._instrumentsSuppliesRequired,
    };
  }

  /**
   * Create a new instance from DTO
   */
  public static fromDTO(dto: BuildCartDTO): BuildCartEntity {
    return new BuildCartEntity(dto);
  }

  /**
   * Create a new instance for a case
   */
  public static createForCase(
    caseId: string,
    traysRequired: number,
    instrumentsSuppliesRequired: number
  ): BuildCartEntity {
    return new BuildCartEntity({
      caseId,
      currentPhase: BuildCartPhase.CART,
      traysRequired,
      instrumentsSuppliesRequired,
      traysLoaded: 0,
      instrumentsSuppliesLoaded: 0,
    });
  }
}
